// live.js - One Server-Sent Events connection per dashboard tab, shared by every
// component that wants to know when something arrives.
//
// Two rules shaped this:
//
//   1. One connection, not one per component. EventSource holds a socket open,
//      and a hook that opened its own would mean the inbox, the order page and
//      the nav badge each holding one. Everything subscribes to a single stream
//      that opens on the first listener and closes after the last one leaves.
//   2. It must degrade to polling. SSE dies quietly behind a proxy that buffers,
//      and a live feature that silently stops being live is worse than one that
//      was never live. If the stream will not stay up, `stale` starts ticking
//      and callers fall back to asking on a timer.
//
// LEGEND
//   1. the shared connection   open, retry, close
//   2. useLive()               subscribe to an event type
//   3. useLiveOrPoll()         useLive, with a timer for when SSE is not working

import { useCallback, useEffect, useRef, useState } from "react";
import { adminApi } from "./api.js";

////////// 1. THE SHARED CONNECTION //////////

const listeners = new Map(); // type -> Set<handler>
let source = null;
let retries = 0;
let retryTimer = null;
let statusHandlers = new Set();
let status = "idle"; // idle | connecting | open | retrying | unsupported

const setStatus = (next) => {
  if (status === next) return;
  status = next;
  for (const handler of statusHandlers) handler(next);
};

const emit = (type, payload) => {
  for (const handler of listeners.get(type) || []) {
    try { handler(payload); } catch { /* one bad listener must not stop the rest */ }
  }
};

function open() {
  if (source || typeof window === "undefined") return;
  if (typeof EventSource === "undefined") { setStatus("unsupported"); return; }

  setStatus(retries ? "retrying" : "connecting");
  source = new EventSource(adminApi.streamUrl(), { withCredentials: true });

  source.addEventListener("ready", () => { retries = 0; setStatus("open"); });

  for (const type of ["message.inbound", "message.status", "order.updated"]) {
    source.addEventListener(type, (event) => {
      let payload = null;
      try { payload = JSON.parse(event.data); } catch { payload = null; }
      emit(type, payload);
    });
  }

  source.onerror = () => {
    // EventSource retries on its own, but only for a transport blip. A 401 or a
    // proxy that refuses the content type produces an endless quiet loop, so it
    // is closed and reopened on a backoff that gives up announcing itself as
    // live after a few attempts.
    source?.close();
    source = null;
    retries += 1;
    setStatus("retrying");
    clearTimeout(retryTimer);
    if (listeners.size) {
      retryTimer = setTimeout(open, Math.min(30_000, 2000 * 2 ** Math.min(retries, 4)));
    }
  };
}

function closeIfIdle() {
  if (listeners.size) return;
  clearTimeout(retryTimer);
  source?.close();
  source = null;
  retries = 0;
  setStatus("idle");
}

////////// 2. SUBSCRIBE //////////

// Takes one type or several. Several rather than calling the hook in a loop,
// which would break the moment a caller passed a list that changed length.

export function useLive(types, handler) {
  const saved = useRef(handler);
  saved.current = handler;
  const list = Array.isArray(types) ? types : [types];
  const key = list.filter(Boolean).join("|");

  useEffect(() => {
    const names = key ? key.split("|") : [];
    if (!names.length) return undefined;

    const wrapped = (payload) => saved.current?.(payload);
    for (const name of names) {
      if (!listeners.has(name)) listeners.set(name, new Set());
      listeners.get(name).add(wrapped);
    }
    open();

    return () => {
      for (const name of names) {
        const set = listeners.get(name);
        set?.delete(wrapped);
        if (set && !set.size) listeners.delete(name);
      }
      closeIfIdle();
    };
  }, [key]);
}

export function useLiveStatus() {
  const [value, setValue] = useState(status);
  useEffect(() => {
    statusHandlers.add(setValue);
    setValue(status);
    return () => { statusHandlers.delete(setValue); };
  }, []);
  return value;
}

////////// 3. LIVE, OR A TIMER //////////
// `onChange` fires when the server says something happened, and also on a timer
// whenever the stream is not open. The timer is slow while SSE is healthy and
// only becomes the real mechanism when it is not, so a proxy that eats the
// stream costs a few seconds of latency rather than the whole feature.
//
// Both paths stop while the tab is hidden. Nobody needs a refresh they cannot
// see, and a laptop in a bag should not poll all night.

export function useLiveOrPoll(types, onChange, { interval = 8000 } = {}) {
  const saved = useRef(onChange);
  saved.current = onChange;
  const connection = useLiveStatus();

  const fire = useCallback((payload) => saved.current?.(payload), []);
  useLive(types, fire);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const period = connection === "open" ? 60_000 : interval;
    let timer = null;

    const tick = () => {
      if (document.visibilityState === "visible") saved.current?.(null);
    };
    const start = () => {
      clearInterval(timer);
      timer = setInterval(tick, period);
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") { tick(); start(); }
      else clearInterval(timer);
    };

    start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [connection, interval]);

  return connection;
}
